import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Network, ExternalLink, AlertTriangle, RotateCw } from "lucide-react";
import StatusDot from "./StatusDot";
import {
  getDashboardLinks,
  normalizeDashboardLinks,
} from "../../api/services/hierarchyServices";

/* Labels for catalog `lab_type` values; unknown values fall through as-is. */
const LAB_TYPE_LABELS = {
  single_vm_docker: "Single VM · Docker",
  multi_vm_proxmox: "Multi VM · Proxmox",
};

const DIFFICULTY_BADGES = {
  easy: "text-emerald-400 bg-emerald-500/10 border-emerald-500/20",
  medium: "text-amber-400 bg-amber-500/10 border-amber-500/20",
  hard: "text-red-400 bg-red-500/10 border-red-500/20",
};

const COLUMNS = ["Box", "Category", "Difficulty", "Type", "Topology", "Link"];

function BoxRow({ box }) {
  const badge =
    DIFFICULTY_BADGES[box.difficulty?.toLowerCase()] ??
    "text-(--color-text-secondary) bg-white/5 border-(--color-border)";

  return (
    <tr className="hover:bg-white/2.5 transition-colors">
      <td className="px-4.5 py-2.5 border-t border-(--color-border)">
        <div className="flex items-center gap-2.25">
          <Network size={14} className="text-(--color-primary) shrink-0" />
          <span className="text-[13px] text-(--color-text-primary) font-medium">
            {box.name}
          </span>
        </div>
      </td>
      <td className="px-4.5 py-2.5 border-t border-(--color-border) text-[12px] text-(--color-text-secondary) max-w-90">
        <span className="line-clamp-1">{box.category || "—"}</span>
      </td>
      <td className="px-4.5 py-2.5 border-t border-(--color-border)">
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold border ${badge}`}
        >
          {box.difficulty || "Unrated"}
        </span>
      </td>
      <td className="px-4.5 py-2.5 border-t border-(--color-border) text-[12px] text-(--color-text-secondary) whitespace-nowrap">
        {LAB_TYPE_LABELS[box.labType] ?? box.labType ?? "—"}
      </td>
      <td className="px-4.5 py-2.5 border-t border-(--color-border)">
        <span className="inline-flex items-center gap-1.5 text-[12px] text-(--color-text-secondary) whitespace-nowrap">
          <StatusDot
            variant={box.hasTopology ? "live" : "idle"}
            pulse={false}
            size={6}
          />
          {box.hasTopology ? "Topology" : "No topology"}
        </span>
      </td>
      <td className="px-4.5 py-2.5 border-t border-(--color-border) text-right">
        {box.href ? (
          <Link
            to={box.href}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[12px] font-semibold text-(--color-primary) bg-(--color-primary)/8 border border-(--color-primary)/20 hover:bg-(--color-primary)/14 transition-colors whitespace-nowrap"
          >
            Open
            <ExternalLink size={12} />
          </Link>
        ) : (
          <span className="text-[12px] text-(--color-text-muted)">
            Not available
          </span>
        )}
      </td>
    </tr>
  );
}

/* Same error treatment the dashboard sections use for a failed section fetch. */
function PanelError({ message, onRetry }) {
  return (
    <div className="flex flex-col items-center justify-center text-center gap-3 px-5 py-9">
      <div className="w-11 h-11 rounded-full flex items-center justify-center bg-red-500/10 border border-red-500/20">
        <AlertTriangle size={20} className="text-red-400" />
      </div>
      <div>
        <div className="text-[13px] font-semibold text-(--color-text-primary)">
          Couldn’t load this section
        </div>
        <div className="text-[12px] text-(--color-text-muted) mt-0.5">
          {message || "Something went wrong fetching the data."}
        </div>
      </div>
      <button
        onClick={onRetry}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-semibold text-(--color-primary) bg-(--color-primary)/8 border border-(--color-primary)/20 hover:bg-(--color-primary)/14 hover:-translate-y-px transition-all"
      >
        <RotateCw size={13} />
        Try again
      </button>
    </div>
  );
}

export default function BoxLinksPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reload, setReload] = useState(0);
  const retry = () => setReload((n) => n + 1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const raw = await getDashboardLinks();
        if (!cancelled) setData(normalizeDashboardLinks(raw));
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reload]);

  return (
    <div className="qv-card qv-noise mt-3.5">
      <div className="flex justify-between items-center px-4.5 py-4 border-b border-(--color-border)">
        <h3 className="text-xs font-semibold text-(--color-text-secondary) flex items-center gap-2">
          Showing boxes which you can directly open
        </h3>
      </div>

      {error ? (
        <PanelError message={error} onRetry={retry} />
      ) : loading || !data ? (
        <div className="p-4.5 flex flex-col gap-3">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="h-9 rounded bg-white/5 animate-pulse" />
          ))}
        </div>
      ) : data.length === 0 ? (
        <div className="px-4.5 py-8 text-[13px] text-(--color-text-muted) text-center">
          No boxes to display.
        </div>
      ) : (
        <div className="max-h-70 overflow-auto">
          <table className="w-full border-collapse">
            <thead className="sticky top-0 z-10 bg-(--color-surface)">
              <tr>
                {COLUMNS.map((h) => (
                  <th
                    key={h}
                    className="text-[11.5px] font-bold text-(--color-text-primary) px-4.5 py-2.5 text-left"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((box) => (
                <BoxRow key={box.id} box={box} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
